import * as THREE from 'three';
import { G, M, TINT, HP, glowMat, mesh, bevel, plate, rib, mergeBones, stages, boneG } from './kit';
/* ============================================================
   TIEFE 深淵 —— BOSS・不完整的存在
   ============================================================

   剪影：從地洞伸出的手臂・沒有身體
   階段：'P1 單臂半握・緩慢橫掃','P2 臂伸長・指節全張','P3 第二隻臂從另一側伸出'

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ── BOSS-22 TIEFE 深淵 ── 本體從頭到尾沒有出現。
   場上只有一隻從地洞伸出來的手臂 —— 前臂、掌、五指，沒有肩，沒有身體。
   地面那個黑色圓盤是它伸出來的地方，也是它唯一留給你的線索。
   P1 單臂、半握、緩慢橫掃。P2 臂整個伸長 3 單位、指節全張。
   P3 第二隻臂從另一側伸出 —— 那底下有多少隻手，遊戲不會告訴你。 */
export function buildTiefe(){
  var GLV = glowMat(TINT.vio.hex, 2.5);
  var root = new THREE.Group(), hands = [], h, i;
  for (h = 0; h < 2; h++){
    var sgn = h ? -1 : 1;
    var hole = mesh(G.cyl, M.void, [4.60, 0.20, 4.60], [sgn*2.60, 0.06, sgn*0.80]);
    hole.userData.noMerge = 1; root.add(hole);
    var ring = mesh(G.tor, GLV, [5.00,5.00,0.20], [sgn*2.60, 0.10, sgn*0.80], [HP,0,0]);
    ring.userData.noMerge = 1; root.add(ring);
    var arm = boneG(root, sgn*2.60, 0.20, sgn*0.80);
    arm.rotation.z = -sgn*0.30;
    arm.add(mesh(G.cyl, M.armor, [1.50,5.60,1.50], [0, 2.80, 0]));
    arm.add(mesh(G.tor, M.trim,  [2.10,2.10,0.50], [0, 1.20, 0], [HP,0,0]));
    arm.add(mesh(G.tor, M.trim,  [1.90,1.90,0.50], [0, 3.60, 0], [HP,0,0]));
    arm.add(rib (M.armor, 0.16, 4.80, 0.16, [ 0.78, 2.80, 0.42]));
    arm.add(rib (M.armor, 0.16, 4.80, 0.16, [-0.78, 2.80, 0.42]));
    var palm = boneG(arm, 0, 5.60, 0);
    palm.add(bevel(M.dark, 2.20, 0.90, 1.90, [0, 0.40, 0]));
    palm.add(plate(M.armor, 2.30, 0.44, 1.10, [0, 0.82, 0.40], -0.32));
    var pv = mesh(G.oct, M.void, [1.20,0.60,1.20], [0, 0.30, 0]);
    pv.userData.noMerge = 1; palm.add(pv);
    var fing = [];
    for (i = 0; i < 5; i++){                        // 又是五 —— 跟 NAMENLOS 同一個作者
      var a = (i - 2) * 0.42;
      var f1 = boneG(palm, Math.sin(a)*0.95, 0.80, Math.cos(a)*0.55);
      f1.rotation.y = a;
      f1.add(mesh(G.cyl, M.armor, [0.40,1.90,0.40], [0, 0.95, 0]));
      var f2 = boneG(f1, 0, 1.90, 0);
      f2.add(mesh(G.cone, M.armor, [0.34,1.70,0.34], [0, 0.85, 0]));
      var fg = mesh(G.oct, GLV, [0.26,0.26,0.26], [0, 0.10, 0.18]);
      fg.userData.noMerge = 1; f2.add(fg);
      fing.push({f1:f1, f2:f2, i:i});
    }
    hands.push({arm:arm, palm:palm, fing:fing, hole:hole, ring:ring, sgn:sgn});
  }
  root.userData.rig = {hands:hands, GLV:GLV};
  return mergeBones(root);
}
export function updTiefe(rig, o, dt){
  stages(rig, o, dt, 1.5);
  var s1 = rig.s1, s2 = rig.s2, h, i, H, F;
  for (h = 0; h < 2; h++){
    H = rig.hands[h];
    var live = h === 0 ? 1 : s2;                    // 第二隻手三階段才出現
    H.arm.visible = live > 0.05;
    H.hole.visible = H.arm.visible;
    H.ring.visible = H.arm.visible;
    if (!H.arm.visible) continue;
    H.arm.scale.y = (0.62 + 0.38*live) * (1 + 0.54*s1);
    H.arm.rotation.z = -H.sgn*(0.30 - 0.16*s1) + 0.16*Math.sin(o.t*0.6 + h*2);
    H.arm.rotation.x = 0.10*Math.sin(o.t*0.5 + h) - o.tel*0.20 + o.atk*0.40;
    H.arm.rotation.y = 0.55*Math.sin(o.t*0.42 + h*2.1)*(1 + 0.6*s1);
    H.palm.rotation.x = -0.30 + 0.40*Math.sin(o.t*0.9 + h);
    for (i = 0; i < 5; i++){
      F = H.fing[i];
      var grip = 0.95 - 0.80*s1 + o.tel*0.55 - o.atk*1.10;
      F.f1.rotation.x = grip + 0.16*Math.sin(o.t*1.7 + i*1.1 + h*3);
      F.f2.rotation.x = grip*0.85 + 0.20*Math.sin(o.t*2.1 + i*1.6);
    }
    H.ring.scale.set(5.00*(1 + 0.10*Math.sin(o.t*1.3 + h)), 5.00, 0.20);
  }
  rig.GLV.emissiveIntensity = 2.5 + 2.6*o.tel + 6*o.atk*(1-o.atk) + 1.2*s2;
}